import { Container, DisplayObject, Graphics } from "pixi.js"
import type { TileMapMetaProvider } from "game-common/tile_map_meta_provider"
import { BODY_GROUND_OFFSET } from "game-common/lpc/body_placement"
import { floorElevation, STOREY_RISE_PX } from "game-common/room/ember/two_storey_house"
import { drawnFloorRects, FloorRect } from "./floor_art"
import { hasFloorArt } from "./floor_opening_edges"
import { storeyCellAlpha } from "./storey_visibility"
import { buildingShadowConfig } from "./building_shadow"

/**
 * THE EDGES OF UPPER FLOORS, DRAWN. A storey is its floor art lifted by the storey rise, and the floors
 * of every storey share one plank texture, so where an upper floor simply ends -- no wall under it -- the
 * eye reads one continuous floor (a castle's tower tops ran straight into its wall walk). A 3/4 view
 * shows such an edge the way ground_rise.ts shows a bank:
 *  - the OVERHANG FACE: the floor's thickness at its south (camera-facing) edge, then the floor below
 *    seen in the dark beneath it, down to that floor's plane. It is drawn by the storey the face lands
 *    on and sorted like a wall standing on that edge, so a body in front of the edge draws over it and
 *    one beneath the overhang behind it. Where a wall stands under the edge, the wall's art is the face.
 *  - the LIP, on the upper floor itself: lit along its south, north and east edges (the sun is upper
 *    right, see sun.ts), shaded along the west.
 *  - the STEP SHADOW along its east, west and north sides: the east and west faces stand edge-on to this
 *    camera and the north one faces away, so a tower beside a wall walk showed nothing but a strip of wall
 *    top between two identical floors. A crisp dark line on the lower floor at the step, softening away
 *    from it, says that floor is lower. North, it lies on the floor seen just behind the upper floor's
 *    edge, so only for a storey straight above (further down, that floor is far from the foot).
 * Neither follows the sun's strength, so they read at night too. A face fades with the floor it hangs
 * from (storey_visibility.ts); while that floor is cut away above the player, a faint GHOST of its edge
 * stays, so you can still see where the floor overhead ends.
 *
 * With the sun's shadows on (building_shadow.ts) the cues they now cast for real step back: the east and
 * west step shadows go (the slab's own shadow lies west, and the sun stands to the north-east, so nothing
 * falls east) and the shade under an overhang is drawn at `underWithSun` of its strength (the slab's
 * shadow darkens most of it already). The north step line stays: it says "lower", not "in shade".
 *
 * Live tuning: window.storeyEdges = { enabled, thickness, under, underWithSun, lip, ghost, step, stepCore, stepWidth }, or = false.
 */
export const STOREY_EDGES = { enabled: true, thickness: 10, under: 0.7, underWithSun: 0.5, lip: 0.55, ghost: 0.5, step: 0.35, stepCore: 0.6, stepWidth: 14 }

export const storeyEdgesConfig = (): typeof STOREY_EDGES => {
    if (typeof window === "undefined") return STOREY_EDGES
    const w = window as any
    if (w.storeyEdges === undefined) w.storeyEdges = STOREY_EDGES
    const live = w.storeyEdges
    if (live === false) return { ...STOREY_EDGES, enabled: false }
    return live && typeof live === "object" ? { ...STOREY_EDGES, ...live } : STOREY_EDGES
}

export type EdgeStorey = Pick<TileMapMetaProvider, "roomId" | "twoStoreyHouse" | "resolveTileMapFromLayerId" |
    "isFloorOpeningAt" | "isHiddenTile" | "isStairAt" | "getHousePresentation">

interface StoreyCells {
    storey: EdgeStorey
    roomId: string
    level: number
    elevation: number
    floor(x: number, y: number): boolean
    /** Anything a floor carries on: its floor, a stair, an opening (FloorOpeningEdges rims those) or a wall. */
    solid(x: number, y: number): boolean
    wall(x: number, y: number): boolean
    roof(x: number, y: number): boolean
}

const cellsOf = (storey: EdgeStorey): StoreyCells => {
    const house = storey.twoStoreyHouse
    const walls = new Set((storey.getHousePresentation()?.wallCells ?? []).map(cell => `${cell.x},${cell.y}`))
    const sky = storey.resolveTileMapFromLayerId("sky")
    const floor = (x: number, y: number) => hasFloorArt(storey, x, y)
    const wall = (x: number, y: number) => walls.has(`${x},${y}`)
    return {
        storey, roomId: storey.roomId, level: house?.floor ?? 0, elevation: house ? floorElevation(house) : 0,
        floor, wall,
        solid: (x, y) => floor(x, y) || wall(x, y) || storey.isStairAt(x, y) || storey.isFloorOpeningAt(x, y),
        roof: (x, y) => { const tile = sky?.[x]?.[y]; return !!tile && tile !== "0" },
    }
}

/** A floor carries on past its edge onto (x, y): its own art, or the roof below standing at its height. */
const carriesOn = (upper: StoreyCells, beneath: StoreyCells | undefined, x: number, y: number): boolean =>
    upper.solid(x, y) || (!!beneath && beneath.level === upper.level - 1 && beneath.roof(x, y))

/** The band of a cell's floor art its south edge belongs to (the lowest of drawnFloorRects). */
const southBand = (rects: FloorRect[]): FloorRect => rects.reduce((low, rect) => rect.y1 > low.y1 ? rect : low)

export interface OverhangFace {
    /** The storey whose floor overhangs. */
    roomId: string
    /** The first slab cell of the run (all share its row). */
    x: number
    y: number
    x0: number
    x1: number
    /** Where the face meets the drawing storey's floor, in that storey's pixels: its sort line. */
    edge: number
    /** How far the face hangs: from the overhanging floor's plane down to the drawing storey's. */
    drop: number
}

interface UpperStorey { upper: StoreyCells; beneath?: StoreyCells; between: StoreyCells[]; drop: number }

/** The upper floors above `below`, each with the storey straight under it and those in between. */
const storeysAbove = (base: StoreyCells, uppers: EdgeStorey[]): UpperStorey[] => {
    const stack = uppers.map(cellsOf).filter(storey => storey.level > base.level).sort((a, b) => a.level - b.level)
    return stack.flatMap((upper, index) => {
        const between = stack.slice(0, index)
        const drop = upper.elevation - base.elevation
        if (upper.level < 1 || drop <= 0) return []
        return [{ upper, between, drop, beneath: [base, ...between].find(storey => storey.level === upper.level - 1) }]
    })
}

/** Whatever hangs down from a higher floor at (x, y) comes down on `base`: nothing in between catches it. */
const landsOn = (base: StoreyCells, between: StoreyCells[], x: number, y: number): boolean =>
    !between.some(storey => storey.floor(x, y) || storey.wall(x, y) || storey.roof(x, y)) &&
    !base.roof(x, y) && (base.level === 0 || base.floor(x, y))

/**
 * The faces `below` draws for the floors stacked above it: each south edge of an upper floor with no
 * wall standing under it and nothing in between, landing on `below`'s floor (the ground always counts).
 * Runs of neighbouring cells merge.
 */
export function overhangFaces(below: EdgeStorey, uppers: EdgeStorey[]): OverhangFace[] {
    const base = cellsOf(below)
    const faces: OverhangFace[] = []
    for (const { upper, beneath, between, drop } of storeysAbove(base, uppers)) {
        const floorAt = (x: number, y: number) => upper.floor(x, y)
        upper.storey.resolveTileMapFromLayerId("map2")?.forEach((column, x) => column?.forEach((_, y) => {
            if (!upper.floor(x, y) || carriesOn(upper, beneath, x, y + 1)) return
            // The storey straight under the edge stands a wall there: the wall's art is the face.
            if (beneath?.wall(x, y)) return
            // A storey in between catches the face, and draws it itself.
            if (!landsOn(base, between, x, y)) return
            const band = southBand(drawnFloorRects(x, y, floorAt))
            faces.push({ roomId: upper.roomId, x, y, x0: band.x0, x1: band.x1, edge: band.y1, drop })
        }))
    }
    return mergeRuns(faces)
}

export interface StepShadow {
    /** The storey whose side casts it. */
    roomId: string
    side: "east" | "west" | "north"
    /** The line the shadow starts from, in the drawing storey's pixels: an x east and west, a y north. */
    at: number
    /** Its span along that line. */
    from: number
    to: number
}

/**
 * The shadows `below` draws at the foot of the east, west and north sides of the floors above it, where
 * the floor beside them comes down on `below`. Pieces of one side that touch merge into one.
 */
export function stepShadows(below: EdgeStorey, uppers: EdgeStorey[]): StepShadow[] {
    const base = cellsOf(below)
    const shadows: StepShadow[] = []
    for (const { upper, beneath, between, drop } of storeysAbove(base, uppers)) {
        const floorAt = (x: number, y: number) => upper.floor(x, y)
        const adjacent = upper.level === base.level + 1
        upper.storey.resolveTileMapFromLayerId("map2")?.forEach((column, x) => column?.forEach((_, y) => {
            if (!upper.floor(x, y)) return
            const steps = (dx: number, dy: number) => !carriesOn(upper, beneath, x + dx, y + dy) && landsOn(base, between, x + dx, y + dy)
            const rects = drawnFloorRects(x, y, floorAt)
            for (const [side, dx] of [["east", 1], ["west", -1]] as const) {
                if (!steps(dx, 0)) continue
                for (const rect of rects) {
                    shadows.push({ roomId: upper.roomId, side, at: dx > 0 ? rect.x1 : rect.x0, from: rect.y0, to: rect.y1 })
                }
            }
            if (adjacent && steps(0, -1)) {
                const top = rects.reduce((high, rect) => rect.y0 < high.y0 ? rect : high)
                shadows.push({ roomId: upper.roomId, side: "north", at: top.y0 - drop, from: top.x0, to: top.x1 })
            }
        }))
    }
    const sorted = shadows.sort((a, b) => a.roomId.localeCompare(b.roomId) || a.side.localeCompare(b.side) ||
        a.at - b.at || a.from - b.from)
    const merged: StepShadow[] = []
    for (const shadow of sorted) {
        const last = merged[merged.length - 1]
        if (last && last.roomId === shadow.roomId && last.side === shadow.side && last.at === shadow.at &&
            last.to === shadow.from) last.to = shadow.to
        else merged.push({ ...shadow })
    }
    return merged
}

/** Tilemaps are walked column by column; join the pieces of one row that touch. */
const mergeRuns = (faces: OverhangFace[]): OverhangFace[] => {
    const sorted = [...faces].sort((a, b) => a.roomId.localeCompare(b.roomId) || a.y - b.y || a.x0 - b.x0)
    const runs: OverhangFace[] = []
    for (const face of sorted) {
        const last = runs[runs.length - 1]
        if (last && last.roomId === face.roomId && last.y === face.y && last.edge === face.edge &&
            last.drop === face.drop && last.x1 === face.x0) last.x1 = face.x1
        else runs.push({ ...face })
    }
    return runs
}

export type LipSide = "north" | "east" | "south" | "west"
export interface SlabLip { side: LipSide; x0: number; y0: number; x1: number; y1: number }

/** The lit and shaded rims of an upper floor along every side where it ends (but under its walls), in its own pixels. */
export function slabLips(storey: EdgeStorey, below?: EdgeStorey): SlabLip[] {
    const cells = cellsOf(storey)
    if (cells.level < 1) return []
    const beneath = below ? cellsOf(below) : undefined
    const lips: SlabLip[] = []
    const floorAt = (x: number, y: number) => cells.floor(x, y)
    storey.resolveTileMapFromLayerId("map2")?.forEach((column, x) => column?.forEach((_, y) => {
        // A wall standing on the cell is its edge already.
        if (!cells.floor(x, y) || cells.wall(x, y)) return
        const open = (dx: number, dy: number) => !carriesOn(cells, beneath, x + dx, y + dy)
        const north = open(0, -1), south = open(0, 1), west = open(-1, 0), east = open(1, 0)
        if (!north && !south && !west && !east) return
        const rects = drawnFloorRects(x, y, floorAt)
        if (south) { const band = southBand(rects); lips.push({ side: "south", x0: band.x0, y0: band.y1 - 2, x1: band.x1, y1: band.y1 }) }
        if (north) {
            const band = rects.reduce((high, rect) => rect.y0 < high.y0 ? rect : high)
            lips.push({ side: "north", x0: band.x0, y0: band.y0, x1: band.x1, y1: band.y0 + 2 })
        }
        for (const rect of rects) {
            if (west) lips.push({ side: "west", x0: rect.x0, y0: rect.y0, x1: rect.x0 + 2, y1: rect.y1 })
            if (east) lips.push({ side: "east", x0: rect.x1 - 1, y0: rect.y0, x1: rect.x1, y1: rect.y1 })
        }
    }))
    return lips
}

const LIGHT = 0xf0c890
const SHADE = 0x0d0a08
const EDGE = 0x4a301c
const EDGE_LIGHT = 0x7a5536
const EDGE_DARK = 0x2a1a10
const LIP_STYLE: Record<LipSide, [number, number]> = {
    // North is the lit back rim over the step shadow behind it: light over dark reads as the step.
    south: [LIGHT, 1], north: [LIGHT, 1], east: [LIGHT, 0.65], west: [SHADE, 0.8],
}

/** Sorted like a wall on the face's foot: bodies whose soles stand south of it draw over it. */
export const overhangFaceDepth = (face: Pick<OverhangFace, "edge">): number => 100 + face.edge - BODY_GROUND_OFFSET

interface DrawnRun { face: OverhangFace; art: Graphics; ghost: Graphics }
interface DrawnSteps { roomId: string; art: Graphics }

/**
 * One storey's share of the edges: the lips of its own floor (one Graphics just above its tilemaps)
 * and the faces of the floors above that land on it (one Graphics per run, each sorted among the
 * storey's bodies and walls, so they are children of its surface, not of a container of their own).
 */
export class StoreyEdgeLayer {
    private lips = new Graphics()
    private runs: DrawnRun[] = []
    private steps: DrawnSteps[] = []
    private version?: string

    constructor() {
        this.lips.interactive = false
        // Tilemaps draw at zero, opening rims at 0.5; bodies, walls and stairs from 100.
        this.lips.zIndex = 0.6
    }

    /** Everything this layer put into the surface, for a map handing its surface over. */
    displayObjects = (): DisplayObject[] => [this.lips, ...this.runs.flatMap(run => [run.art, run.ghost]),
        ...this.steps.map(steps => steps.art)]

    getFaces = (): readonly OverhangFace[] => this.runs.map(run => run.face)

    /** Rebuild when this storey or one above changed, then fade each face with the floor it hangs from. */
    sync(provider: TileMapMetaProvider | undefined, surface: Container): void {
        const edges = storeyEdgesConfig()
        const sun = !!buildingShadowConfig().enabled
        const config = sun ? { ...edges, under: edges.under * (edges.underWithSun ?? STOREY_EDGES.underWithSun) } : edges
        const uppers: TileMapMetaProvider[] = []
        for (let upper = provider?.getUpperFloorProvider(); upper && upper !== provider && !uppers.includes(upper);
            upper = upper.getUpperFloorProvider()) uppers.push(upper)
        const below = provider?.getFloorSupportProvider()
        const stamp = (p?: TileMapMetaProvider) => p ? `${p.roomId}:${p.getPresentationVersion()}:${p.twoStoreyHouse?.floor}` : ""
        const version = provider && config.enabled ? [sun, config.thickness, config.under, config.lip, config.step,
            config.stepCore, config.stepWidth, STOREY_RISE_PX,
            stamp(provider), stamp(below), ...uppers.map(stamp)].join("|") : undefined
        if (version !== this.version) {
            this.version = version
            this.rebuild(version ? provider : undefined, below, uppers, config, sun)
        }
        for (const child of this.displayObjects()) if (child.parent !== surface) surface.addChild(child)
        for (const run of this.runs) {
            const alpha = storeyCellAlpha(run.face.roomId, run.face.x, run.face.y)
            run.art.alpha = alpha
            run.art.visible = alpha > 0.01
            run.ghost.alpha = (1 - alpha) * config.ghost
            run.ghost.visible = run.ghost.alpha > 0.01
        }
        // A storey cut away over the player still stands there: its step shadows follow the storey, not the cut.
        for (const steps of this.steps) {
            steps.art.alpha = storeyCellAlpha(steps.roomId, NaN, NaN)
            steps.art.visible = steps.art.alpha > 0.01
        }
    }

    private rebuild(provider: TileMapMetaProvider | undefined, below: TileMapMetaProvider | undefined,
        uppers: TileMapMetaProvider[], config: typeof STOREY_EDGES, sun = false): void {
        this.lips.clear()
        this.runs.forEach(run => { run.art.destroy(); run.ghost.destroy() })
        this.runs = []
        this.steps.forEach(steps => steps.art.destroy())
        this.steps = []
        if (!provider?.twoStoreyHouse) return
        for (const lip of slabLips(provider, below)) {
            const [color, strength] = LIP_STYLE[lip.side]
            this.lips.beginFill(color, config.lip * strength).drawRect(lip.x0, lip.y0, lip.x1 - lip.x0, lip.y1 - lip.y0).endFill()
        }
        for (const face of overhangFaces(provider, uppers)) {
            const art = new Graphics(), ghost = new Graphics()
            art.interactive = ghost.interactive = false
            art.zIndex = overhangFaceDepth(face)
            ghost.zIndex = art.zIndex + 0.01
            drawFace(art, face, config)
            ghost.beginFill(LIGHT, 1).drawRect(face.x0, face.edge - face.drop - 1, face.x1 - face.x0, 2).endFill()
            this.runs.push({ face, art, ghost })
        }
        for (const shadow of stepShadows(provider, uppers)) {
            // The sun casts the east and west ones for real now (or, east, none at all).
            if (sun && shadow.side !== "north") continue
            let steps = this.steps.find(drawn => drawn.roomId === shadow.roomId)
            if (!steps) {
                steps = { roomId: shadow.roomId, art: new Graphics() }
                steps.art.interactive = false
                // On the floor, over the lips and opening rims; under every wall and body.
                steps.art.zIndex = 0.7
                this.steps.push(steps)
            }
            drawStepShadow(steps.art, shadow, config)
        }
    }

    destroy(): void {
        this.lips.destroy()
        this.runs.forEach(run => { run.art.destroy(); run.ghost.destroy() })
        this.runs = []
        this.steps.forEach(steps => steps.art.destroy())
        this.steps = []
        this.version = undefined
    }
}

const drawFace = (g: Graphics, face: OverhangFace, config: typeof STOREY_EDGES): void => {
    const top = face.edge - face.drop, width = face.x1 - face.x0
    const thickness = Math.min(config.thickness, face.drop)
    // The floor below, seen beneath the overhang: darkest deep under the slab, easing toward the open edge.
    const steps = 8, from = top + thickness
    for (let i = 0; i < steps; i++) {
        const y0 = Math.round(from + (face.edge - from) * i / steps), y1 = Math.round(from + (face.edge - from) * (i + 1) / steps)
        if (y1 > y0) g.beginFill(SHADE, config.under * (1 - i / steps)).drawRect(face.x0, y0, width, y1 - y0).endFill()
    }
    // Contact shadow where the overhang's shade meets the open floor.
    g.beginFill(SHADE, 0.18).drawRect(face.x0, face.edge, width, 3).endFill()
    // The floor's own thickness, its top edge catching the light.
    g.beginFill(EDGE, 1).drawRect(face.x0, top, width, thickness).endFill()
    if (thickness >= 3) {
        g.beginFill(EDGE_LIGHT, 1).drawRect(face.x0, top + 1, width, 1).endFill()
        g.beginFill(EDGE_DARK, 1).drawRect(face.x0, top + thickness - 1, width, 1).endFill()
    }
}

/** A crisp line at the foot of the step, then a shade easing away over the lower floor. */
const drawStepShadow = (g: Graphics, shadow: StepShadow, config: typeof STOREY_EDGES): void => {
    const length = shadow.to - shadow.from
    for (let i = 0; i < config.stepWidth; i++) {
        const alpha = i < 2 ? config.stepCore : config.step * Math.pow(1 - (i - 2) / Math.max(1, config.stepWidth - 2), 1.6)
        if (alpha <= 0) continue
        g.beginFill(SHADE, alpha)
        if (shadow.side === "north") g.drawRect(shadow.from, shadow.at - i - 1, length, 1)
        else g.drawRect(shadow.side === "east" ? shadow.at + i : shadow.at - i - 1, shadow.from, 1, length)
        g.endFill()
    }
}
